import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ContextItem } from "@/api/ai/types";
import type { AiChatSidebarProps, AttachmentKey } from "./AiChatSidebar";
import { getDefaultAttachments } from "./AiChatSidebar";
import { buildContextItems } from "./buildContextItems";
import {
  followsViewAttachments,
  isTruncated,
  pipelinePresent,
} from "./contextSelection";
import { useAiChat } from "./useAiChat";

function toggleSetItem<T>(set: Set<T>, item: T): Set<T> {
  const next = new Set(set);
  if (next.has(item)) {
    next.delete(item);
  } else {
    next.add(item);
  }
  return next;
}

function deriveAvailability(props: AiChatSidebarProps) {
  return {
    hasInput: !!props.inputDocument?.trim(),
    hasSchema: !!props.schema?.trim(),
    hasError: !!props.lastError?.trim(),
    hasTests: (props.testCases ?? []).length > 0,
    hasPipeline: pipelinePresent(props.pipelineView),
    // Sent as text with its parse error, not as the structured item.
    pipelineUnparsed: !!props.pipelineView?.parseError,
  };
}

// The follows-view mode's state: what the user hid, sticky for the
// conversation; the attachments are everything available minus that.
// The reset is tied to the two ways of leaving a conversation (New chat,
// picking another) — not to the conversation id, which a new chat swaps
// from a local id to the engine's on its first send.
function useHiddenItems(props: AiChatSidebarProps) {
  const [excluded, setExcluded] = useState<Set<AttachmentKey>>(new Set());
  const toggle = useCallback(
    (key: AttachmentKey) => setExcluded((prev) => toggleSetItem(prev, key)),
    [],
  );
  const reset = useCallback(() => setExcluded(new Set()), []);
  return {
    attachments: followsViewAttachments(props, excluded),
    toggle,
    reset,
  };
}

// Items the engine will cut at its line cap — the bar says so.
function truncatedItems(props: AiChatSidebarProps): Set<AttachmentKey> {
  const keys = new Set<AttachmentKey>();
  if (isTruncated(props.inputDocument)) keys.add("input");
  if (isTruncated(props.schema)) keys.add("schema");
  for (const s of props.scripts ?? []) {
    if (isTruncated(s.content)) keys.add(`script:${s.name}`);
  }
  return keys;
}

// The attach mode's state (the old Studio): the chips pick what to send,
// re-derived on an error, a test failure or a change of the script set,
// and reset after every message.
function useAttachItems(props: AiChatSidebarProps, testFailureCount: number) {
  const [attachments, setAttachments] = useState<Set<AttachmentKey>>(() =>
    getDefaultAttachments(props),
  );
  // The set of scripts on offer — a script staged after mount must join
  // the defaults (hosts that attach scripts by default).
  const scriptNames = (props.scripts ?? [])
    .filter((s) => s.content.trim())
    .map((s) => s.name)
    .join("|");
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only reset on error/test-failure/script-set change
  useEffect(() => {
    setAttachments(getDefaultAttachments(props));
  }, [props.lastError, testFailureCount, scriptNames]);
  const toggle = useCallback(
    (key: AttachmentKey) => setAttachments((prev) => toggleSetItem(prev, key)),
    [],
  );
  const afterSend = useCallback(
    () => setAttachments(getDefaultAttachments(props)),
    [props],
  );
  return { attachments, toggle, afterSend };
}

type Chat = ReturnType<typeof useAiChat>;

// Leaving a conversation (New chat, picking another) runs `onLeave` first —
// the follows-view mode starts the next one with nothing hidden.
function useLeaveConversation(chat: Chat, onLeave: () => void) {
  const newConversation = useCallback(() => {
    onLeave();
    chat.newConversation();
  }, [onLeave, chat.newConversation]);
  const selectConversation = useCallback(
    async (id: string) => {
      onLeave();
      await chat.selectConversation(id);
    },
    [onLeave, chat.selectConversation],
  );
  return { newConversation, selectConversation };
}

const noop = () => {};

// A host-queued message (the editors' "Fix with AI") goes out through the
// same submit as a typed one — with this view's context and prompt — once
// the input holds it. The ref makes the send idempotent: the sidebar often
// mounts for the message (the helper was collapsed), and a mount effect
// can run again before the clear has landed.
function useSendQueuedMessage(chat: Chat, handleSubmit: () => void) {
  const sent = useRef<string | null>(null);
  useEffect(() => {
    if (chat.queuedMessage === null) {
      sent.current = null;
      return;
    }
    if (chat.inputValue !== chat.queuedMessage) return;
    if (sent.current === chat.queuedMessage) return;
    sent.current = chat.queuedMessage;
    chat.clearQueuedMessage();
    handleSubmit();
  }, [
    chat.queuedMessage,
    chat.inputValue,
    chat.clearQueuedMessage,
    handleSubmit,
  ]);
}

export function useAiChatSidebarState(props: AiChatSidebarProps) {
  const followsView = props.contextMode === "follows-view";
  const chat = useAiChat();
  const testFailureCount =
    props.testCases?.filter((tc) => tc.passed === false).length ?? 0;
  const hidden = useHiddenItems(props);
  const attach = useAttachItems(props, testFailureCount);
  const mode = followsView
    ? { ...hidden, afterSend: noop, onLeave: hidden.reset }
    : { ...attach, onLeave: noop };
  const { attachments } = mode;
  const truncated = useMemo(() => truncatedItems(props), [props]);
  const leave = useLeaveConversation(chat, mode.onLeave);

  const nonEmptyScripts = useMemo(
    () => (props.scripts ?? []).filter((s) => s.content.trim()),
    [props.scripts],
  );

  const availability = deriveAvailability(props);

  const handleSubmit = useCallback(() => {
    if (!chat.inputValue.trim()) return;
    const context: ContextItem[] = buildContextItems(props, attachments);
    chat.sendUserMessageStream(context, props.systemPrompt);
    mode.afterSend();
  }, [
    chat.inputValue,
    props,
    attachments,
    chat.sendUserMessageStream,
    mode.afterSend,
  ]);

  useSendQueuedMessage(chat, handleSubmit);

  return {
    attachments,
    truncated,
    testFailureCount,
    toggleAttachment: mode.toggle,
    nonEmptyScripts,
    ...availability,
    handleSubmit,
    ...chat,
    ...leave,
  };
}
